/**
 * ScrollReveal - Framer Motion powered scroll-triggered animations
 * Use for fade-up, fade-in, stagger, scale, and custom variants
 */

import React from 'react';
import { motion, useInView } from 'framer-motion';
import { cn } from '../../lib/utils';

const defaultVariants = {
  fadeUp: {
    hidden: { opacity: 0.3, y: 20 }, // Start visible but slightly faded
    visible: { opacity: 1, y: 0 },
  },
  fadeIn: {
    hidden: { opacity: 0.5 }, // Start semi-visible
    visible: { opacity: 1 },
  },
  fadeLeft: {
    hidden: { opacity: 0.3, x: -20 },
    visible: { opacity: 1, x: 0 },
  },
  fadeRight: {
    hidden: { opacity: 0.3, x: 20 },
    visible: { opacity: 1, x: 0 },
  },
  scaleIn: {
    hidden: { opacity: 0.5, scale: 0.98 },
    visible: { opacity: 1, scale: 1 },
  },
  slideUp: {
    hidden: { opacity: 0.3, y: 40 },
    visible: { opacity: 1, y: 0 },
  },
};

const defaultTransition = {
  duration: 0.6,
  ease: [0.16, 1, 0.3, 1],
};

/**
 * @param {string} variant - 'fadeUp' | 'fadeIn' | 'fadeLeft' | 'fadeRight' | 'scaleIn' | 'slideUp'
 * @param {number} delay - Stagger delay in seconds
 * @param {number} threshold - Visibility threshold 0-1
 * @param {number} amount - How much of element must be visible (0-1)
 */
export function ScrollReveal({
  children,
  variant = 'fadeUp',
  delay = 0,
  threshold = 0.1,
  amount = 0.2,
  className,
  as: Component = motion.div,
  transition,
  ...props
}) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: amount ?? threshold, margin: '0px 0px -5% 0px' });

  const variants = defaultVariants[variant] || defaultVariants.fadeUp;
  const mergedTransition = {
    ...defaultTransition,
    delay,
    ...transition,
  };

  // Start visible on mobile to prevent loading issues
  const [isMobile, setIsMobile] = React.useState(false);
  React.useEffect(() => {
    setIsMobile(window.innerWidth < 768);
  }, []);

  return (
    <Component
      ref={ref}
      initial={isMobile ? "visible" : "hidden"} // Start visible on mobile
      animate={isInView || isMobile ? 'visible' : 'hidden'} // Always visible on mobile
      variants={variants}
      transition={mergedTransition}
      className={cn(className)}
      {...props}
    >
      {children}
    </Component>
  );
}

/**
 * StaggerContainer - Parent for staggered children
 */
export function StaggerContainer({
  children,
  className,
  staggerDelay = 0.08,
  threshold = 0.1,
  ...props
}) {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.05, margin: '0px 0px -5% 0px' });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={{
        visible: {
          transition: {
            staggerChildren: staggerDelay,
            delayChildren: 0.1,
          },
        },
      }}
      className={cn(className)}
      {...props}
    >
      {children}
    </motion.div>
  );
}

/**
 * StaggerItem - Child for StaggerContainer
 */
export function StaggerItem({
  children,
  variant = 'fadeUp',
  className,
  ...props
}) {
  const itemVariants = defaultVariants[variant] || defaultVariants.fadeUp;

  return (
    <motion.div
      variants={itemVariants}
      transition={defaultTransition}
      className={cn(className)}
      {...props}
    >
      {children}
    </motion.div>
  );
}
